import {updateObject} from '@/store/utils'
import {ModuleFc} from '@/project'
import {sumBy, cloneDeep} from 'lodash'
import {DateTime} from 'luxon'
import {findTransferItem} from './utils'
import {TransferState, RequestInfo, OtpStatus, TransferResults, TransferItem} from './types'
import {EventProperty} from '../gtm/types'

export {TransferState, TransferItem}

const initState: TransferState = {
  requestInfo: {
    workflow: null,
    task: null,
    otpUrl: null,
  },
  otpStatus: {
    status: null,
    expiredAt: null,
    otpValue: null,
    transferList: [],
  },
  transferStatus: {
    id: null,
    code: null,
    name: null,
    isTriggered: null,
    triggeredAt: null,
    isCanceled: null,
    canceledAt: null,
    isFinished: null,
    finishedAt: null,
    progressValue: null,
  },
  transfer: [],
  bankInfo: {
    id: null,
    code: null,
    number: null,
    isTransferPasswordInputRequired: null
  },
  transferResults: {
    id: null,
    workflowName: null,
    taskName: null,
    transaction: null,
    dueDate: null,
    requester: null,
    assignees: null,
    disposed: null,
    disposer: null,
    disposedAt: null,
    errorCode: null,
    transferList: [],
  },
  transferTime: {
    current: null,
    startBankCheck: null,
    startOtpCheck: null,
  },
}

const module: ModuleFc<TransferState> = (ctx) => ({
  namespaced: true,
  state: cloneDeep(initState),
  getters: {
    transferIds(state) {
      return state.transfer.map((transfer) => transfer.id)
    },
    totalAmount(state) {
      if(state.transfer.length === 0) {
        return 0
      }
      return sumBy(state.transfer, 'amount')
    },
    elapsedTime(state): number {
      if(state.transferTime.current && state.transferTime.startBankCheck) {
        return state.transferTime.current.diff(state.transferTime.startBankCheck || 0).milliseconds
      }
      return 0
    },
    totalTransferFee(state) {
      if(state.otpStatus?.transferList?.length === 0) {
        return 0
      }
      return sumBy(state.otpStatus.transferList, 'transferFee')
    },
    gtmInfo(state, getters): EventProperty {
      return {
        workflow: 'transfer',
        quantity: state.transfer.length,
        amount: getters.totalAmount,
        id: getters.transferIds
      }
    }
  },
  actions: {
    async requestTransfer({commit, rootState}, payload) {
      const {
        bizId = rootState.biz.id,
        transactions, bankAccount, bankAccountPassword, transferPassword,
      } = payload
      const result = await ctx.$axios({
        url: '/transfer',
        method: 'post',
        data: {
          bizId,
          transactions,
          bankAccount,
          bankAccountPassword,
          transferPassword,
        },
      })
      commit('updateRequestTransfer', result.data)
    },
    async getOtpStatus({commit, state}) {
      const {otpUrl} = state.requestInfo
      const result = await ctx.$axios({
        url: '/otp',
        method: 'get',
        data: {otpUrl},
      })
      commit('updateOtpStatus', result.data)
    },
    async transferOtpValue({commit, state}, payload) {
      const {requestInfo} = state
      const {value} = payload
      const result = await ctx.$axios({
        url: '/otp',
        method: 'put',
        data: {
          otpUrl: requestInfo.otpUrl,
          otpValue: value
        },
      })
      commit('updateOtpStatus', result.data)
    },
    async getTransferResults({commit, rootState, state}, payload: {bizId?: string, task: string}) {
      const {bizId = rootState.biz?.id, task = state.requestInfo?.task} = payload
      const result = await ctx.$axios({
        url: '/transfer-results',
        method: 'get',
        data:{bizId, task}
      })
      commit('updateTransferResults', result.data)
    },
  },
  mutations: {
    updateTransfer(state, payload: any[]) {
      state.transfer = [...payload]
    },
    updateBankInfo(state, payload: any) {
      state.bankInfo = payload
    },
    updateRequestTransfer(state, payload: RequestInfo) {
      state.requestInfo = payload
    },
    updateOtpStatus(state, payload: OtpStatus) {
      const {transferList = []} = payload
      state.otpStatus = {
        ...payload,
        transferList: transferList?.map((item) => {
          const transferItem: any = findTransferItem(state.transfer, item)
          return {
            ...item,
            outAccountMemo: transferItem?.bizSummary,
            inAccountMemo: transferItem?.clientSummary,
            clientName: transferItem?.clientName,
          }
        }) || []
      }
    },
    updateTransferResults(state, payload: TransferResults) {
      const {transferList = []} = payload
      state.transferResults = {
        ...payload,
        transferList: transferList?.map((item) => {
          const transferItem: any = findTransferItem(state.transfer, item)
          return {
            ...item,
            clientName: transferItem?.clientName
          }
        }) || []
      }
    },
    updateCurrentTime(state) {
      state.transferTime.current = DateTime.local()
    },
    updateStartTime(state, stepName) {
      state.transferTime[stepName] = state.transferTime.current
    },
    clear(state) {
      updateObject(state, initState)
    },
    clearTransferTime(state) {
      state.transferTime = cloneDeep(initState.transferTime)
    }
  },
})

export default module
